import { escapeHTML } from '../../utils/escape.js';
import { formatPrice, formatFechaHora, formatFechaDia } from '../../utils/formatters.js';

/**
 * Admin "Gift cards" section (openspec/changes/staff-giftcards/spec.md B4,
 * B5): cards by state with each state's count and total (computed by SQL),
 * and "Anular" with a motivo. Pure render; `js/giftcards.js` owns state.
 */

const BOTON = 'px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors disabled:opacity-60';

const VACIO = {
    vigentes: 'No hay gift cards vigentes.',
    canjeadas: 'Todavía no se canjeó ninguna.',
    vencidas: 'No hay gift cards vencidas.',
    anuladas: 'No hay gift cards anuladas.',
};

/**
 * @param {{
 *   uiState: { status: 'loading'|'error'|'ready', mensaje?: string },
 *   estado: 'vigentes'|'canjeadas'|'vencidas'|'anuladas',
 *   estados: Array<{ value: string, label: string }>,
 *   items: Array<Record<string, any>>,
 *   totales: Record<string, { cantidad: number, monto: number }>,
 *   anulando: string | null,
 *   enviando: boolean,
 *   motivo?: string,
 *   etiquetaMedio: (medio: string) => string,
 * }} props
 */
export function AdminGiftcardsView({ uiState, estado, estados, items, totales, anulando, enviando, motivo = '', etiquetaMedio }) {
    const filtros = estados
        .map(({ value, label }) => {
            const activo = value === estado;
            const t = totales[value];
            const cifras = t ? `<span class="block mt-1 text-sm font-black tabular-nums normal-case tracking-normal">${t.cantidad} · ${escapeHTML(formatPrice(t.monto))}</span>` : '';
            return `<button type="button" data-action="giftcards-filtro" data-estado="${value}" aria-pressed="${activo}"
                class="px-4 py-3 rounded-xl text-left text-[11px] font-black uppercase tracking-widest transition-colors ${activo ? 'bg-brand text-white shadow-lg' : 'bg-surface border border-border-subtle text-text-muted hover:text-brand'}">${label}${cifras}</button>`;
        })
        .join('');

    let cuerpo;
    if (uiState.status === 'loading') {
        cuerpo = `<div class="py-16 text-center" role="status" aria-live="polite"><i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand" aria-hidden="true"></i><p class="mt-3 text-xs font-black uppercase tracking-widest text-text-muted">Cargando gift cards...</p></div>`;
    } else if (uiState.status === 'error') {
        cuerpo = `<div class="py-16 text-center" role="alert"><p class="text-sm font-bold text-text-primary mb-6">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar las gift cards.')}</p><button type="button" data-action="giftcards-reintentar" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button></div>`;
    } else if (items.length === 0) {
        cuerpo = `<p class="py-12 text-center text-sm text-text-muted">${VACIO[estado]}</p>`;
    } else {
        const filas = items
            .map((g) => {
                const id = escapeHTML(g.id);
                // The state comes from SQL (one owner); voidable = not redeemed, not voided.
                const anulable = g.estado === 'vigentes' || g.estado === 'vencidas';
                const detalle = [
                    `${escapeHTML(etiquetaMedio(g.medio))}`,
                    `Emitida el ${escapeHTML(formatFechaHora(g.emitida_at))}`,
                    `Válida hasta el ${escapeHTML(formatFechaDia(g.valida_hasta))}`,
                    g.canjeada_at ? `Canjeada el ${escapeHTML(formatFechaHora(g.canjeada_at))}` : '',
                    g.anulada_at ? `Anulada el ${escapeHTML(formatFechaHora(g.anulada_at))}` : '',
                ]
                    .filter(Boolean)
                    .join(' · ');
                const formAnular =
                    anulando === g.id
                        ? `<form data-action-form="giftcard-anular" data-id="${id}" novalidate class="mt-3 flex flex-col gap-3">
                            <label class="flex flex-col gap-2">
                                <span class="text-[11px] font-black uppercase tracking-widest text-text-muted">Motivo</span>
                                <textarea name="motivo" rows="2" class="w-full px-4 py-3 rounded-xl border border-border-subtle bg-base text-text-primary text-sm">${escapeHTML(motivo)}</textarea>
                            </label>
                            <div data-giftcards-aviso-anular></div>
                            <div class="flex gap-2">
                                <button type="button" data-action="giftcard-anular-cancelar" class="${BOTON}" ${enviando ? 'disabled' : ''}>Cancelar</button>
                                <button type="submit" class="px-4 py-2 rounded-xl bg-red-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-60" ${enviando ? 'disabled' : ''}>${enviando ? 'Anulando...' : 'Anular gift card'}</button>
                            </div>
                        </form>`
                        : '';
                return `
                    <article data-giftcard-id="${id}" class="py-5 border-b border-border-subtle last:border-b-0">
                        <div class="flex flex-wrap items-start justify-between gap-4">
                            <div class="min-w-0">
                                <p class="text-base font-black text-text-primary break-words">Para ${escapeHTML(g.para)}</p>
                                ${g.de_parte ? `<p class="text-sm text-text-muted break-words">De parte de ${escapeHTML(g.de_parte)}</p>` : ''}
                                <p class="mt-1 text-xs text-text-muted">${detalle}</p>
                                ${g.anulada_motivo ? `<p class="mt-2 text-sm text-text-primary"><span class="font-black">Motivo:</span> ${escapeHTML(g.anulada_motivo)}</p>` : ''}
                            </div>
                            <div class="flex items-center gap-3">
                                <p class="text-xl font-black tabular-nums text-text-primary">${escapeHTML(formatPrice(g.monto))}</p>
                                ${g.estado === 'vigentes' ? `<button type="button" data-action="giftcard-imagen" data-id="${id}" class="${BOTON}">Imagen</button>` : ''}
                                ${anulable && anulando !== g.id ? `<button type="button" data-action="giftcard-anular" data-id="${id}" class="${BOTON}">Anular</button>` : ''}
                            </div>
                        </div>
                        ${formAnular}
                    </article>`;
            })
            .join('');
        cuerpo = `<div class="bg-surface border border-border-subtle rounded-2xl px-5">${filas}</div>`;
    }

    return `
        <section data-giftcards-admin>
            <header class="mb-6">
                <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Gift cards</h2>
                <p class="mt-1 text-sm text-text-muted max-w-2xl">Las que se vendieron en el mostrador. Una gift card no se borra: se canjea o se anula con motivo.</p>
            </header>
            <div role="group" aria-label="Filtrar por estado" class="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2">${filtros}</div>
            <div data-giftcards-aviso></div>
            ${cuerpo}
        </section>
    `;
}
